'use client'

/**
 * UgcActionsMenu — the per-item "⋮" overflow menu on any user-generated content
 * (content comments, community posts, community comments). Offers Report (opens
 * the shared ReportDialog) and Block / Unblock the author. Mirrors the mobile
 * app's UgcActionsMenu so web + iOS satisfy the same App Store UGC requirement.
 *
 * Renders nothing for signed-out viewers or on the viewer's OWN content
 * (you can't report/block yourself). Block/unblock write straight to
 * `blocked_users` (RLS scopes every row to blocker_user_id = auth.uid()); the
 * host component filters blocked authors out of its list via onBlockChange.
 *
 * A small self-contained menu (button + click-outside) — the repo's
 * ui/dropdown-menu has no other consumers, so we don't take that dependency.
 */

import { useEffect, useRef, useState } from 'react'
import { useTranslations } from 'next-intl'
import { MoreVertical, Flag, Ban, RotateCcw, Trash2, Pencil } from 'lucide-react'
import { createClient } from '@/lib/supabase/client'
import { ReportDialog } from './report-dialog'
import type { ReportContentType } from '@/lib/moderation/report-reasons'

export function UgcActionsMenu({
  contentType,
  contentId,
  authorUserId,
  currentUserId,
  isBlocked = false,
  onBlockChange,
  canEdit = false,
  onEdit,
  editLabel = 'Edit',
  canDelete = false,
  onDelete,
  deleteLabel = 'Delete',
}: {
  contentType: ReportContentType
  contentId: string
  authorUserId: string
  currentUserId: string | null | undefined
  isBlocked?: boolean
  /** Called after a successful block/unblock so the host can update its
   *  blocked-id set and re-filter its list. */
  onBlockChange?: (userId: string, blocked: boolean) => void
  /** Show an Edit row — the author on their own content. The host owns the
   *  inline edit form + save. */
  canEdit?: boolean
  onEdit?: () => void
  editLabel?: string
  /** Show a destructive Delete row — the author on their own content, or a
   *  moderator on anyone's. The host owns the confirm + soft-delete. */
  canDelete?: boolean
  onDelete?: () => void
  deleteLabel?: string
}) {
  const t = useTranslations('moderation')
  const supabase = createClient()
  const [open, setOpen] = useState(false)
  const [reporting, setReporting] = useState(false)
  const [busy, setBusy] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const isOwn = currentUserId === authorUserId
  // Signed-out → no menu. On your own content the menu shows only when there
  // is an own-action to offer (edit or delete) — you can't report or block
  // yourself.
  if (!currentUserId || (isOwn && !canDelete && !canEdit)) return null

  const toggleBlock = async () => {
    setBusy(true)
    try {
      if (isBlocked) {
        // RLS scopes the delete to the current blocker.
        await supabase.from('blocked_users').delete().eq('blocked_user_id', authorUserId)
        onBlockChange?.(authorUserId, false)
      } else {
        const { error } = await supabase
          .from('blocked_users')
          .insert({ blocker_user_id: currentUserId, blocked_user_id: authorUserId })
        // 23505 = already blocked → treat as success.
        if (!error || error.code === '23505') onBlockChange?.(authorUserId, true)
      }
    } finally {
      setBusy(false)
      setOpen(false)
    }
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-label={t('menuAria')}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-muted"
      >
        <MoreVertical className="h-4 w-4" aria-hidden />
      </button>
      {open && (
        <div role="menu" className="absolute right-0 z-30 mt-1 w-44 border border-border bg-card py-1 text-sm shadow-lg">
          {!isOwn && (
            <button
              role="menuitem"
              type="button"
              onClick={() => {
                setReporting(true)
                setOpen(false)
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted"
            >
              <Flag className="h-3.5 w-3.5" aria-hidden /> {t('menuReport')}
            </button>
          )}
          {!isOwn && (
            <button
              role="menuitem"
              type="button"
              onClick={toggleBlock}
              disabled={busy}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted disabled:opacity-50"
            >
              {isBlocked ? <RotateCcw className="h-3.5 w-3.5" aria-hidden /> : <Ban className="h-3.5 w-3.5" aria-hidden />}
              {isBlocked ? t('menuUnblock') : t('menuBlock')}
            </button>
          )}
          {canEdit && onEdit && (
            <button
              role="menuitem"
              type="button"
              onClick={() => {
                onEdit()
                setOpen(false)
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted"
            >
              <Pencil className="h-3.5 w-3.5" aria-hidden /> {editLabel}
            </button>
          )}
          {canDelete && onDelete && (
            <button
              role="menuitem"
              type="button"
              onClick={() => {
                onDelete()
                setOpen(false)
              }}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-red-600 hover:bg-muted"
            >
              <Trash2 className="h-3.5 w-3.5" aria-hidden /> {deleteLabel}
            </button>
          )}
        </div>
      )}
      <ReportDialog
        open={reporting}
        onClose={() => setReporting(false)}
        contentType={contentType}
        contentId={contentId}
        reportedUserId={authorUserId}
        currentUserId={currentUserId}
      />
    </div>
  )
}
